
import React from 'react';
import { Button } from "@/components/ui/button";
import { Link2, Copy, Download, MessageSquarePlus, History, Video, Search, Eye } from "lucide-react";
import AIChatInterface from './AIChatInterface';
import TextToSpeechButton from './TextToSpeechButton';
import ColorPicker from '../ColorPicker';
import TextRecoveryDialog from './TextRecoveryDialog';
import FormulaSelector from './FormulaSelector';
import SpeechToTextButton from './SpeechToTextButton';
import OCRButton from './OCRButton';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { insertTextAtCursor } from '@/utils/textInsertionUtils';
import { TextHistoryItem } from './NoteState';
import { trackFeatureUsed } from '@/services/appsflyer';

interface EditorToolbarProps {
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLinkClick: () => void;
  onColorSelect: (color: string) => void;
  selectedColor: string;
  content: string;
  onContentUpdate: (content: string) => void;
  onCopy: () => void;
  onExportPDF: () => void;
  onExportExcel: () => void;
  onExportDoc: () => void;
  onExportMarkdown: () => void;
  onCommentClick: () => void;
  onMeetingTranscriptionClick: () => void;
  onSearchClick: () => void;
  textHistory: TextHistoryItem[];
  showRecoveryDialog: boolean;
  setShowRecoveryDialog: (show: boolean) => void;
  isWysiwygMode: boolean;
  onToggleWysiwyg: () => void;
  selectedText?: { start: number; end: number; text: string; } | null;
  contentEditableRef?: React.RefObject<HTMLDivElement>;
  noteId?: string | null;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  onLinkClick,
  noteId,
  onColorSelect,
  selectedColor,
  content,
  onContentUpdate,
  onCopy,
  selectedText,
  onExportPDF,
  onExportExcel,
  onExportDoc,
  onExportMarkdown,
  onCommentClick,
  onMeetingTranscriptionClick,
  onSearchClick,
  textHistory,
  showRecoveryDialog,
  setShowRecoveryDialog,
  isWysiwygMode,
  onToggleWysiwyg,
  contentEditableRef,
}) => {
  const handleFormulaCharSelect = (character: string) => {
    const textareaElement = document.querySelector('textarea');
    
    if (textareaElement) {
      // Use the utility function to insert text at cursor position
      insertTextAtCursor(textareaElement, character, content, onContentUpdate);
    } else {
      console.error("No textarea element found for formula insertion");
      onContentUpdate(content + character);
    }
  };

  const handleTranscriptionComplete = (text: string) => {
    trackFeatureUsed('speech_to_text');
    console.log("Transcription received:", text);
    const textareaElement = document.querySelector('textarea');
    
    if (textareaElement) {
      // Insert at cursor position with proper spacing
      insertTextAtCursor(textareaElement, text + " ", content, onContentUpdate);
    } else {
      console.error("No textarea element found for transcription insertion");
      // Append to end if no cursor position is available
      onContentUpdate((content ? content + " " : "") + text);
    }
  };

  const handleOCRTextExtracted = (text: string) => {
    trackFeatureUsed('ocr');
    console.log("OCR text extracted:", text);
    const textareaElement = document.querySelector('textarea');
    
    if (textareaElement) {
      // Insert at cursor position with proper spacing
      insertTextAtCursor(textareaElement, text + "\n\n", content, onContentUpdate);
    } else {
      console.error("No textarea element found for OCR text insertion");
      // Append to end if no cursor position is available
      onContentUpdate((content ? content + "\n\n" : "") + text);
    }
  };

  return (
    <div className="space-y-4 overflow-x-hidden max-w-full">
      <div className="flex justify-between items-center flex-wrap gap-2 overflow-x-hidden max-w-full">
        <div className="flex flex-wrap gap-2 max-w-full">
          <Button variant="ghost" size="icon" onClick={onLinkClick}>
            <Link2 className="h-4 w-4" />
          </Button>

          <Button variant="ghost" size="icon" onClick={onCopy}>
            <Copy className="h-4 w-4" />
          </Button>

          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowRecoveryDialog(true)}
            disabled={textHistory.length === 0}
          >
            <History className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleWysiwyg}
            title={isWysiwygMode ? "Switch to Markdown view" : "Switch to WYSIWYG view"}
          >
            <Eye className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={onSearchClick}
            title="Search and replace"
          >
            <Search className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onExportPDF}>
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportExcel}>
                Export as Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportDoc}>
                Export as DOC
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportMarkdown}>
                Export as Markdown
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onCommentClick}
            title="Add comment"
          >
            <MessageSquarePlus className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={onMeetingTranscriptionClick}
            title="Meeting transcription"
          >
            <Video className="h-4 w-4" />
          </Button>




          <FormulaSelector onCharacterSelect={handleFormulaCharSelect} />
          <TextToSpeechButton text={content} />
          <SpeechToTextButton onTranscriptionComplete={handleTranscriptionComplete} />
          <OCRButton onTextExtracted={handleOCRTextExtracted} />
          <ColorPicker onColorSelect={onColorSelect} selectedColor={selectedColor} />
        </div>
      </div>
      
      <TextRecoveryDialog
        showDialog={showRecoveryDialog}
        onOpenChange={setShowRecoveryDialog}
        textHistory={textHistory}
        onRestore={onContentUpdate}
        currentContent={content}
      />
      
      <AIChatInterface 
        content={content} 
        onContentUpdate={onContentUpdate} 
        selectedText={selectedText}
        noteColor={selectedColor}
        noteId={noteId}
      />
    </div>
  );
};

export default EditorToolbar;
